import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ErrorBoundary } from './app/components';
import './index.css';
import { initI18n, getSavedLanguage } from './app/lib/i18n/config';

// Initialize i18n with saved language (if any) with error handling
const savedLang = getSavedLanguage();

const initApp = async () => {
  try {
    // Add timeout to prevent hanging
    const initPromise = initI18n(savedLang ?? undefined);
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('i18n initialization timeout')), 5000)
    );
    
    await Promise.race([initPromise, timeoutPromise]);
    console.log('i18n initialized successfully');
  } catch (error) {
    console.error('Failed to initialize i18n, using fallback:', error);
    // Continue anyway - i18n will use default language
  }
  
  // Render app regardless of i18n success
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Root element not found');
    return;
  }
  
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>,
  );
};

// Register Service Worker for PWA support
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('[SW] Service Worker registered with scope:', registration.scope);

        // Check for service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available; refresh to get it
                console.log('[SW] New content available, refreshing...');
                window.location.reload();
              }
            });
          }
        });
      })
      .catch((error) => {
        console.error('[SW] Service Worker registration failed:', error);
      });
  });

  // Handle service worker messages
  navigator.serviceWorker.addEventListener('message', (event) => {
    console.log('[SW] Message from service worker:', event.data);
  });
}

// Start the app
initApp().catch((error) => {
  console.error('Failed to initialize app:', error);
  // Try to render anyway
  const rootElement = document.getElementById('root');
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <ErrorBoundary>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ErrorBoundary>
      </React.StrictMode>,
    );
  }
});
